<p-toast></p-toast>
<p-progressBar mode="indeterminate" class="m-2" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
    <app-page-hero eyebrow="Reports" title="Tax Report" subtitle="Review VAT collected, paid, and payable or refundable balances.">
        <div actions class="report-actions">
            <p-button label="Generate" icon="pi pi-refresh" (onClick)="loadTaxReportData({})"></p-button>
            <p-button label="Export" icon="pi pi-file-excel" severity="success" (onClick)="exportExcel()"></p-button>
            <p-button label="Download PDF" icon="pi pi-file-pdf" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('download')" (onClick)="printReport(true)"></p-button>
            <p-button label="Print" icon="pi pi-print" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('print')" (onClick)="printReport()"></p-button>
            <app-report-email reportName="Tax Report" [rows]="taxReportItems"></app-report-email>
        </div>
    </app-page-hero>

    <app-page-form-section icon="pi pi-filter" title="Report Filters" subtitle="Choose the tax reporting date range.">
        <div class="report-filter-grid two-column">
            <p-iftalabel>
                <p-datePicker inputId="startDate" [(ngModel)]="startDate" dateFormat="yy-mm-dd" (onSelect)="loadTaxReportData({})" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="startDate">Start Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-datePicker inputId="endDate" [(ngModel)]="endDate" dateFormat="yy-mm-dd" (onSelect)="loadTaxReportData({})" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="endDate">End Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <app-logistics-project-lookup inputId="logisticsProjectId" [(ngModel)]="selectedLogisticsProjectId" (ngModelChange)="loadTaxReportData({})"></app-logistics-project-lookup>
                <label for="logisticsProjectId">Logistics Project</label>
            </p-iftalabel>
        </div>
    </app-page-form-section>

<app-page-data-panel *ngIf="diagnostics.length" title="Report Diagnostics" [resultText]="diagnostics.length + ' checks'">
    <p-table [value]="diagnostics" styleClass="p-datatable-gridlines" [tableStyle]="{ 'min-width': '60rem' }">
        <ng-template pTemplate="header">
            <tr>
                <th>Severity</th>
                <th>Code</th>
                <th>Message</th>
                <th>Reference</th>
                <th class="text-right">Amount</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-diagnostic>
            <tr>
                <td>{{ diagnostic.severity }}</td>
                <td>{{ diagnostic.code }}</td>
                <td>{{ diagnostic.message }}</td>
                <td>{{ diagnostic.referenceNumber || diagnostic.referenceId || '-' }}</td>
                <td class="text-right">{{ diagnostic.amount ?? '-' }}</td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

<div class="grid">
    <div class="col-12 md:col-4">
        <p-card header="Total VAT Collected" styleClass="text-center">
            <div class="text-2xl font-bold">
                {{ totalVATCollectedSum | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
            </div>
        </p-card>
    </div>
    <div class="col-12 md:col-4">
        <p-card header="Total VAT Paid" styleClass="text-center">
            <div class="text-2xl font-bold">
                {{ totalVATPaidSum | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
            </div>
        </p-card>
    </div>
    <div class="col-12 md:col-4">
        <p-card header="Net VAT (Payable / Refundable)" styleClass="text-center">
            <div class="text-2xl font-bold" [ngClass]="{'text-green-600': netVATSum >= 0, 'text-red-600': netVATSum < 0}">
                {{ netVATSum | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
            </div>
        </p-card>
    </div>
</div>


<!-- VAT on Sales Table -->
<app-page-data-panel title="VAT on Sales">
    <p-table [value]="vatOnSalesItems" dataKey="vatSubCategory" [resizableColumns]="true" styleClass="p-datatable-gridlines" [tableStyle]="{ 'min-width': '75rem' }">
        <ng-template pTemplate="header">
            <tr>
                <th style="width: 30%;">VAT SubCategory</th>
                <th class="text-right">Amount ({{ reportCurrencyCode }})</th>
                <th class="text-right">Adjustment ({{ reportCurrencyCode }})</th>
                <th class="text-right">VAT Collected ({{ reportCurrencyCode }})</th>
                <th class="text-right">VAT Paid ({{ reportCurrencyCode }})</th>
                <th class="text-right">Net VAT ({{ reportCurrencyCode }})</th>
                <th>Trace</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr>
                <td>{{ item.vatSubCategory }}</td>
                <td class="text-right">{{ item.amountSAR | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.adjustmentSAR | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.vatCollected | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.vatPaid | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right" [ngClass]="{'font-bold': item.netVAT !== 0}">
                    {{ item.netVAT | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </td>
                <td><button pButton type="button" icon="pi pi-share-alt" class="p-button-text p-button-sm" (click)="openVatTrace(item)"></button></td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr>
                <td class="font-bold text-right">Total VAT on Sales:</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Sales', 'amountSAR') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Sales', 'adjustmentSAR') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Sales', 'vatCollected') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Sales', 'vatPaid') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Sales', 'netVAT') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

<!-- VAT on Purchases Table -->
<app-page-data-panel title="VAT on Purchases">
    <p-table [value]="vatOnPurchasesItems" dataKey="vatSubCategory" [resizableColumns]="true" styleClass="p-datatable-gridlines" [tableStyle]="{ 'min-width': '75rem' }">
        <ng-template pTemplate="header">
            <tr>
                <th style="width: 30%;">VAT SubCategory</th>
                <th class="text-right">Amount ({{ reportCurrencyCode }})</th>
                <th class="text-right">Adjustment ({{ reportCurrencyCode }})</th>
                <th class="text-right">VAT Collected ({{ reportCurrencyCode }})</th>
                <th class="text-right">VAT Paid ({{ reportCurrencyCode }})</th>
                <th class="text-right">Net VAT ({{ reportCurrencyCode }})</th>
                <th>Trace</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr>
                <td>{{ item.vatSubCategory }}</td>
                <td class="text-right">{{ item.amountSAR | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.adjustmentSAR | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.vatCollected | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.vatPaid | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right" [ngClass]="{'font-bold': item.netVAT !== 0}">
                    {{ item.netVAT | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </td>
                <td><button pButton type="button" icon="pi pi-share-alt" class="p-button-text p-button-sm" (click)="openVatTrace(item)"></button></td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr>
                <td class="font-bold text-right">Total VAT on Purchases:</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Purchases', 'amountSAR') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Purchases', 'adjustmentSAR') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Purchases', 'vatCollected') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Purchases', 'vatPaid') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Purchases', 'netVAT') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

<!-- VAT on Journal Entry / Other VAT Items Table -->
<app-page-data-panel title="VAT on Adjustments / Other VAT Items">
    <p-table [value]="vatOnAdjustmentsOtherItems" dataKey="vatSubCategory" [resizableColumns]="true" styleClass="p-datatable-gridlines" [tableStyle]="{ 'min-width': '75rem' }">
        <ng-template pTemplate="header">
            <tr>
                <th style="width: 30%;">VAT SubCategory</th>
                <th class="text-right">Amount ({{ reportCurrencyCode }})</th>
                <th class="text-right">Adjustment ({{ reportCurrencyCode }})</th>
                <th class="text-right">VAT Collected ({{ reportCurrencyCode }})</th>
                <th class="text-right">VAT Paid ({{ reportCurrencyCode }})</th>
                <th class="text-right">Net VAT ({{ reportCurrencyCode }})</th>
                <th>Trace</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr>
                <td>{{ item.vatSubCategory }}</td>
                <td class="text-right">{{ item.amountSAR | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.adjustmentSAR | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.vatCollected | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right">{{ item.vatPaid | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right" [ngClass]="{'font-bold': item.netVAT !== 0}">
                    {{ item.netVAT | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </td>
                <td><button pButton type="button" icon="pi pi-share-alt" class="p-button-text p-button-sm" (click)="openVatTrace(item)"></button></td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr>
                <td class="font-bold text-right">Total Adjustments / Other:</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Journal Entry', 'amountSAR') + getCategoryTotal('Other VAT Items', 'amountSAR') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Journal Entry', 'adjustmentSAR') + getCategoryTotal('Other VAT Items', 'adjustmentSAR') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Journal Entry', 'vatCollected') + getCategoryTotal('Other VAT Items', 'vatCollected') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Journal Entry', 'vatPaid') + getCategoryTotal('Other VAT Items', 'vatPaid') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
                <!-- This is the corrected line -->
                <td class="text-right font-bold">{{ getCategoryTotal('VAT on Journal Entry', 'netVAT') + getCategoryTotal('Other VAT Items', 'netVAT') | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

<div class="flex align-items-center justify-content-between mt-3">
    In total there are {{totalRecords ? totalRecords : 0}} Tax entries.
</div>
</app-page-shell>
